import { useQueryClient } from '@tanstack/react-query'
import App from 'antd/es/app'
import { useCallback, useEffect } from 'react'
import { ApiError, tenantApi } from '../api/client'
import type { ExtensionCollectionCommitted } from '../api/extension-collection-events'
import { attachExtensionCollectionEvents } from '../api/extension-collection-events'
import { useAppState } from './app-state'

export function useExtensionCollectionEvents(enabled: boolean): void {
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const { session } = useAppState()
  const currentTenantId = session?.tenant?.id ?? ''
  const onCommitted = useCallback(
    (event: ExtensionCollectionCommitted) => {
      // New extension builds return the tenant encoded in the device-authenticated
      // collection receipt. Only that explicit server value is allowed to produce
      // a tenant-mismatch warning; a missing job can also be a stale event or an
      // older extension that did not include ownership metadata.
      if (event.tenantId && currentTenantId && event.tenantId !== currentTenantId) {
        void message.warning({
          content:
            '采集任务已提交到其他租户；当前插件仍绑定旧账号。请在当前账号的“插件下载”页重新授权扩展后再采集。',
          key: `foreign-collection-${event.jobId}`,
          duration: 8,
        })
        return
      }
      if (event.tenantId && currentTenantId === event.tenantId) return

      // Legacy extension receipts have no tenant identity. Keep the old job
      // lookup only as a compatibility diagnostic, and never label a 404 as a
      // confirmed tenant mismatch.
      void tenantApi.job(event.jobId).catch((error: unknown) => {
        if (!(error instanceof ApiError) || error.problem.status !== 404) return
        void message.warning({
          content:
            '采集任务已提交，但当前扩展未返回租户归属信息；请更新插件到服务器最新版本后再采集。',
          key: `foreign-collection-${event.jobId}`,
          duration: 8,
        })
      })
    },
    [currentTenantId, message],
  )

  useEffect(() => {
    if (!enabled) return undefined
    return attachExtensionCollectionEvents(queryClient, window, onCommitted)
  }, [enabled, onCommitted, queryClient])
}
